import { WX_ID_LIGHTNING } from "../../../store/WeatherContentsState";
import LightningNowcast from "./LightningNowcast";
import Liden from "./Liden";
import { formatTimeZoneDate, translation } from "../../../utils/FPEUtils";
import LegendLightning from "../legend/LegendLightning";
import TimeController from "../helper/TimeController";
import WRAP from "WRAP-JS";

export const LIGHTNING_TYPE_LIDEN = Liden.NAME;
export const LIGHTNING_TYPE_NOWCAST = LightningNowcast.NAME;

class Lightning {
  static NAME = WX_ID_LIGHTNING;
  //雷タイムコントローラー
  lightningTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;

  validtimeInfo = {
    [LightningNowcast.NAME]: '---',
    [Liden.NAME]: '---'
  }

  typeVisible = {
    [LIGHTNING_TYPE_LIDEN]: true,
    [LIGHTNING_TYPE_NOWCAST]: true
  }

  constructor() {
    this.lightningTimeController = new TimeController(WX_ID_LIGHTNING,{
      label: 'Lightning',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });

    this.lightningNowcast = new LightningNowcast(this.lightningTimeController);
    this.lightningNowcast.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.liden = new Liden(this.lightningTimeController);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    const cascade = (action) => {
      if (action.type === 'SET_CONTENTS_TIME') {
        this.validtimeInfo[action.payload.id] = action.payload.baseTime;
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      } else if (action.type === 'UPDATE_VALID_TIME') {
        this.validtimeInfo[action.payload.id] = action.payload.baseTime;
      } else {
        return this.dispatcher(action);
      }
    };
    this.lightningNowcast.setDispatcher(cascade);
    this.liden.setDispatcher(cascade);
  }

  async drawLayer(visible) {
    this.visible = visible;
    await this.setVisible(this.visible);
  }

  setVisible(visible) {
    return new Promise(resolve => {
      /**
       * LIDENのポーリングが止まってしまうという指摘を受け調査した結果、初回LightningをONにした時、WorkspaceにLightningを保存していた時に本問題が発生しており、
       * NowcastとLIDENのinitializeとsetVisibleの呼び出し順を制御する必要がある事がわかった。
       * LIDENはLayerにコンテンツオプション（end_time, begin_time）が設定されていると、新しい時刻のデータを取得しない。
       * LinksではNowcastとLIDENは同じTimecontrollerで制御されており、LightningのinitializeとsetVisibleでLIDENのLayerにコンテンツオプションが設定されていた。
       * LIDENのsetVisible内で該当のコンテンツオプションを削除する処理が入っていた為、初回表示時の処理順を制御することでポーリンが止まらない様に対応を行った。
       * 1.Nowcast.initialize -> 2.Nowcast.setVisible -> 3.Liden.initialize -> 4.Liden.setVisible
       * ※ 1,2時に処理順によってはLIDENにコンテンツオプションが設定される可能性がある。
       * Nowcastのデータ取得に失敗するとLIDENのデータ取得に問題が無い場合でもLIDENも表示できない。
       */
      this.lightningNowcast.drawLayer(visible && this.typeVisible[LIGHTNING_TYPE_NOWCAST]).finally(() => {
        this.liden.drawLayer(visible && this.typeVisible[LIGHTNING_TYPE_LIDEN]);
        resolve();
      });
    });
  }

  setVisibleWithType(visible, type) {
    this.typeVisible[type] = visible;
    if (!this.visible) {
      return;
    }
    switch (type) {
      case LIGHTNING_TYPE_NOWCAST:
        this.lightningNowcast.drawLayer(visible);
        break;
      case LIGHTNING_TYPE_LIDEN:
        this.liden.drawLayer(visible);
        break;
      default:
    }
    this._updateTimeList();
  }

  resetTypeVisible() {
    if (Object.values(this.typeVisible).filter(v => v).length === 0) {
      this.typeVisible[LIGHTNING_TYPE_NOWCAST] = true;
      this.typeVisible[LIGHTNING_TYPE_LIDEN] = true;
    }
  }

  getLayer() {
    return [this.lightningNowcast.getLayer(), this.liden.getLayer()]
  }

  getTimeInformation() {
    const timeList = [];
    Object.keys(this.typeVisible).forEach(contents => {
      if (this.typeVisible[contents]) {
        timeList.push({
          id: contents,
          type: contents,
          time: this.validtimeInfo[contents]
        });
      }
    });
    return {
      id: Lightning.NAME,
      label: 'Lightning',
      baseTime: timeList,
      baseTimeFormatter: (time) => {
        return `${translation(time.type)} ${formatTimeZoneDate(time.time)}`
      }
    }
  }

  getLegend() {
    return {
      id: Lightning.NAME,
      label: 'Lightning',
      component: LegendLightning,
      contents: this
    };
  }

  isVisible(type) {
    return this.typeVisible[type];
  }

  getLidenContentsVisibleState() {
    return this.liden.getContentsVisibleState()
  }

  setLidenContentsVisible(name, visible) {
    return this.liden.setContentsVisible(name, visible);
  }

  getCoverage() {
    return this.lightningNowcast.getCoverage()
  }

  setCoverage(coverage) {
    return this.lightningNowcast.setCoverage(coverage);
  }


  getTimeController() {
    return this.lightningTimeController;
  }

  applyElapsed(elapsed) {
    this.lightningNowcast.applyElapsed(elapsed);

  }

  applyPlanETD(etd) {
    this.lightningNowcast.applyPlanETD(etd)
  }

  setAutoRefresh(autoFlg) {
    this.autoRefresh = autoFlg;
    this.lightningNowcast.setAutoRefresh(autoFlg);
    this.liden.setAutoRefresh(autoFlg);
  }

  changeRoutePointEditing(routePointEditing) {
    [this.liden, this.lightningNowcast].forEach(contents => {
      if (contents.changeRoutePointEditing) {
        contents.changeRoutePointEditing(routePointEditing);
      }
    });
  }

  _updateTimeList() {
    //LightningNowCastのタイムリストを基準とする
    let validTimes = this.lightningNowcast?.dh?.retained?.validtime?.validtime;
    if (!validTimes) {
      return;
    }

    //過去未来１時間でfilter
    validTimes = Array.from(validTimes).sort();
    // const latest = validTimes[validTimes.length - 1];//未来1時間
    // const latestTime = new WRAP.Core.DateTime(latest)

    const now = new WRAP.Core.DateTime();
    //現在時刻 - 1hour
    const oldest = new WRAP.Core.DateTime();
    oldest.add(-3600);

    this.timeList = validTimes.filter(vt => {
      const time = new WRAP.Core.DateTime(vt);
      if (this.typeVisible[LIGHTNING_TYPE_NOWCAST]) {
        return oldest.date.getTime() <= time.date.getTime();
      } else {
        //NowCastを表示していない場合、未来データを切り捨て
        return oldest.date.getTime() <= time.date.getTime() && now.date.getTime() >= time.date.getTime()
      }
    });

    if (this.autoRefresh) {
      this.lightningTimeController.setCurrentTime(this.validtimeInfo[LightningNowcast.NAME]);
    }
    this.lightningTimeController.adjustCurrentTime();
    this.lightningTimeController.invalidate();
  }
}

export default Lightning